import { Card } from "@rothoblaas/design-system";
import classNames from "classnames";
import { FC, useCallback } from "react";
import useI18n from "../../../hooks/useTranslations";
import { BeamReinforcementResult } from "../../../http/beamReinforcement/formulas";
import styles from "./ResultsCard.module.css";

type ResultsCardProps = {
  result: BeamReinforcementResult | null;
};

const Row: FC<{ label: string; value: string }> = ({ label, value }) => (
  <tr>
    <td>{label}</td>
    <td className={styles.value}>{value}</td>
  </tr>
);

const SectionHead: FC<{ label: string }> = ({ label }) => (
  <tr className={styles.head}>
    <td colSpan={2}>{label}</td>
  </tr>
);

const ResultsCard: FC<ResultsCardProps> = ({ result }) => {
  const i18n = useI18n();
  const common = i18n.translation.common;
  const r = i18n.translation.beamReinforcementModule.results;
  const d = i18n.translation.beamReinforcementModule.details;

  const fmt = useCallback(
    (value: number, decimals = 2): string =>
      Number.isFinite(value)
        ? value.toLocaleString(i18n.translation.languageCode, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          })
        : "—",
    [i18n.translation.languageCode],
  );

  if (!result) {
    return (
      <Card style={{ minHeight: 400 }} accentColor="var(--accent-base, var(--red))" shadow title={r.summaryTitle}>
        <div className={styles.empty}>{r.incompleteWarning}</div>
      </Card>
    );
  }

  const chip = (label: string, value: number) => {
    const bad = !Number.isFinite(value) || value > 100;
    return (
      <div className={classNames(styles.chip, { [styles.chipBad]: bad })}>
        <div className={styles.chipLabel}>{label}</div>
        <div className={styles.chipValue}>{Number.isFinite(value) ? `${fmt(value, 0)}%` : "—"}</div>
        <div className={styles.chipBar}>
          <span
            className={styles.chipBarFill}
            style={{ width: `${Math.min(Number.isFinite(value) ? value : 100, 100)}%` }}
          />
        </div>
      </div>
    );
  };

  const warnings: string[] = [];
  if (result.lef <= 0) warnings.push(r.warningInsufficientPenetration);
  if (result.screw.length > result.lmax) warnings.push(r.warningScrewTooLong(Math.round(result.lmax)));
  if (!result.heightOK) warnings.push(r.warningHeightInsufficient(result.h2, Math.round(result.hmin)));
  if (!result.spacOK) warnings.push(r.warningSpacingInsufficient(result.screwSpacing, Math.round(result.a1min)));
  if (!result.loadOK && result.geomOK) warnings.push(r.warningConnectionOverload(Math.round(result.etaConn * 10) / 10));

  return (
    <Card style={{ minHeight: 400 }} accentColor="var(--accent-base, var(--red))" shadow title={r.summaryTitle}>
      <div className={styles.wrapper}>
        <div className={styles.chips}>
          {chip(r.bendingUtilization, result.etaMmax)}
          {chip(r.shearUtilization, result.etaVmax)}
          {chip(r.connectionUtilization, result.etaConn)}
        </div>

        {warnings.map((warning) => (
          <div key={warning} className={classNames(styles.chip, styles.chipBad)}>
            {warning}
          </div>
        ))}

        <details className={styles.details} open>
          <summary>{d.loadsSection}</summary>
          <table className={styles.table}>
            <tbody>
              <Row label={d.kmod} value={fmt(result.kmod, 2)} />
              <Row label={d.selfWeight} value={`${fmt(result.g1, 3)} ${common.unitKnM}`} />
              <Row label={d.designLoad} value={`${fmt(result.qd, 2)} ${common.unitKnM}`} />
            </tbody>
          </table>
        </details>

        <details className={styles.details}>
          <summary>{d.stiffnessSection}</summary>
          <table className={styles.table}>
            <tbody>
              <Row label="I₁" value={`${fmt(result.i1 / 1e4, 0)} ·10⁴ mm⁴`} />
              <Row label="I₂" value={`${fmt(result.i2 / 1e4, 0)} ·10⁴ mm⁴`} />
              <Row label="ν₁" value={fmt(result.nu1, 3)} />
              <Row label="ν₂" value={fmt(result.nu2, 3)} />
            </tbody>
          </table>
        </details>

        <details className={styles.details}>
          <summary>{d.bendingSection}</summary>
          <table className={styles.table}>
            <tbody>
              <Row label={d.designMoment} value={`${fmt(result.md / 1e6, 2)} ${common.unitKn}·m`} />
              <SectionHead label={d.existingBeamLabel} />
              <Row label={d.bendingStress} value={`${fmt(result.sm1)} ${common.unitMpa}`} />
              <Row label={d.bendingStrength} value={`${fmt(result.fmd1)} ${common.unitMpa}`} />
              <Row label="η_m,1" value={`${fmt(result.etaM1, 0)}%`} />
              <SectionHead label={d.reinforcementBeamLabel} />
              <Row label={d.bendingStress} value={`${fmt(result.sm2)} ${common.unitMpa}`} />
              <Row label={d.bendingStrength} value={`${fmt(result.fmd2)} ${common.unitMpa}`} />
              <Row label="η_m,2" value={`${fmt(result.etaM2, 0)}%`} />
            </tbody>
          </table>
        </details>

        <details className={styles.details}>
          <summary>{d.shearSection}</summary>
          <table className={styles.table}>
            <tbody>
              <Row label={d.designShear} value={`${fmt(result.vd / 1000, 2)} ${common.unitKn}`} />
              <SectionHead label={d.existingBeamLabel} />
              <Row label={d.shearStress} value={`${fmt(result.tau1)} ${common.unitMpa}`} />
              <Row label={d.shearStrength} value={`${fmt(result.fvd1)} ${common.unitMpa}`} />
              <Row label="η_v,1" value={`${fmt(result.etaV1, 0)}%`} />
              <SectionHead label={d.reinforcementBeamLabel} />
              <Row label={d.shearStress} value={`${fmt(result.tau2)} ${common.unitMpa}`} />
              <Row label={d.shearStrength} value={`${fmt(result.fvd2)} ${common.unitMpa}`} />
              <Row label="η_v,2" value={`${fmt(result.etaV2, 0)}%`} />
            </tbody>
          </table>
        </details>

        <details className={styles.details}>
          <summary>{d.axialSection}</summary>
          <table className={styles.table}>
            <tbody>
              <Row label="l_ef" value={`${fmt(result.lef, 1)} ${common.unitMm}`} />
              <Row label="F_head" value={`${fmt(result.fHead / 1000, 2)} ${common.unitKn}`} />
              <Row label="F_thr" value={`${fmt(result.fThr / 1000, 2)} ${common.unitKn}`} />
              <Row label="F_ax,Rk" value={`${fmt(result.faxRk / 1000, 2)} ${common.unitKn}`} />
            </tbody>
          </table>
        </details>

        <details className={styles.details}>
          <summary>{d.johansenSection}</summary>
          <table className={styles.table}>
            <tbody>
              {result.johansen ? (
                <>
                  <Row label="f_h,1,k" value={`${fmt(result.fh1)} ${common.unitMpa}`} />
                  <Row label="f_h,2,k" value={`${fmt(result.fh2)} ${common.unitMpa}`} />
                  <Row label="β" value={fmt(result.johansen.beta, 3)} />
                  <Row label="F_v,Rk" value={`${fmt(result.fvRk / 1000, 2)} ${common.unitKn}`} />
                  <Row label="F_v,Rd" value={`${fmt(result.fvRd / 1000, 2)} ${common.unitKn}`} />
                </>
              ) : (
                <Row label={d.johansenSection} value="—" />
              )}
            </tbody>
          </table>
        </details>

        <details className={styles.details} open>
          <summary>{d.connectionSection}</summary>
          <table className={styles.table}>
            <tbody>
              <Row label={d.screwCount} value={`${result.nvTotal}`} />
              <Row label={d.designShearForce} value={`${fmt(result.vEd / 1000, 2)} ${common.unitKn}`} />
              <Row label={d.connectionResistance} value={`${fmt(result.fvRd / 1000, 2)} ${common.unitKn}`} />
              <Row label="η" value={Number.isFinite(result.etaConn) ? `${fmt(result.etaConn, 1)}%` : "—"} />
            </tbody>
          </table>
        </details>
      </div>
    </Card>
  );
};

export default ResultsCard;
